import {useEffect, useId, useRef, useState} from 'react';
import {Terminal} from '@xterm/xterm';
import {FitAddon} from '@xterm/addon-fit';
import {Unicode11Addon} from '@xterm/addon-unicode11';
import '@xterm/xterm/css/xterm.css';
import {ErrorNotice, Loading} from './components';
import {errorDetails} from './lib/store';
import type {ErrorDetails, NativeTerminalTicket} from './lib/types';
import {PtyResumeSanitizer} from './lib/hermes/pty-resume-sanitizer';
import {isViewportPinnedToBottom, parseResumeControlMessage, shouldFollowPtyOutput} from './lib/hermes/pty-scroll';

type Phase = 'connecting' | 'open' | 'closed' | 'error';
type OpenTerminal = (sessionId: string, agentId: string | null, attachId: string, signal: AbortSignal) => Promise<NativeTerminalTicket>;

// This attachment identity lasts for this browser document. Reconnects and page
// navigation reuse it; another browser tab does not inherit this terminal.
const attachments = new Map<string, string>();
function attachmentId(accountId: string, sessionId: string, agentId: string | null): string {
  const target = JSON.stringify([accountId, sessionId, agentId]);
  let id = attachments.get(target);
  if (!id) {
    id = crypto.randomUUID().replaceAll('-', '');
    attachments.set(target, id);
  }
  return id;
}

// Matches the terminal metrics in the pinned Hermes web ChatPage.
function fontSize(width: number): number {
  if (width < 300) return 7;
  if (width < 360) return 8;
  if (width < 420) return 9;
  if (width < 520) return 10;
  if (width < 720) return 11;
  if (width < 1024) return 12;
  return 14;
}

function terminalError(value: unknown): ErrorDetails {
  if (value !== null && typeof value === 'object' && 'message' in value && typeof value.message === 'string') {
    const details = value as Record<string, unknown>;
    return {name: typeof details.name === 'string' ? details.name : 'HermesTerminalError', message: value.message,
      ...(typeof details.stack === 'string' ? {stack: details.stack} : {}),
      ...(details.cause === undefined ? {} : {cause: details.cause})};
  }
  return errorDetails(new Error(typeof value === 'string' ? value : JSON.stringify(value)));
}

export function NativeTerminal({accountId, sessionId, agentId, openTerminal, compact = false}: {
  accountId: string; sessionId: string; agentId: string | null; openTerminal: OpenTerminal; compact?: boolean;
}) {
  const host = useRef<HTMLDivElement>(null);
  const [phase, setPhase] = useState<Phase>('connecting');
  const [error, setError] = useState<ErrorDetails | null>(null);
  const [reconnect, setReconnect] = useState(0);
  const helpId = useId();

  useEffect(() => {
    const currentHost = host.current;
    if (!currentHost) return;
    const element: HTMLDivElement = currentHost;
    const controller = new AbortController();
    let disposed = false;
    let socket: WebSocket | null = null;
    let connected = false;
    let failure: ErrorDetails | null = null;
    let frame = 0;
    let connectDeadline: ReturnType<typeof setTimeout> | undefined;
    let keepalive: ReturnType<typeof setInterval> | undefined;
    let resumeWindow: ReturnType<typeof setTimeout> | undefined;
    let followOutput = true;
    const decoder = new TextDecoder();
    const resumeSanitizer = new PtyResumeSanitizer();
    setPhase('connecting'); setError(null);

    const terminal = new Terminal({
      allowProposedApi: true, cursorBlink: true, cursorStyle: 'block',
      fontFamily: "'JetBrains Mono', monospace", fontSize: fontSize(element.clientWidth),
      lineHeight: element.clientWidth < 1024 ? 1.02 : 1.15, letterSpacing: 0,
      fontWeight: '400', fontWeightBold: '700', macOptionIsMeta: true,
      macOptionClickForcesSelection: true, rightClickSelectsWord: true,
      scrollback: 5000, screenReaderMode: true, disableStdin: true,
      theme: {background: '#000000', foreground: '#f0e6d2', cursor: '#f0e6d2',
        cursorAccent: '#000000', selectionBackground: '#f0e6d244'},
    });
    const fit = new FitAddon();
    terminal.loadAddon(fit);
    terminal.loadAddon(new Unicode11Addon());
    terminal.unicode.activeVersion = '11';
    // Agent output cannot write the browser clipboard or create active links.
    const clipboardGuard = terminal.parser.registerOscHandler(52, () => true);
    const linkGuard = terminal.parser.registerOscHandler(8, () => true);
    terminal.open(element);
    terminal.textarea?.setAttribute('aria-label', 'Agent terminal input');
    terminal.textarea?.setAttribute('aria-describedby', helpId);

    function fail(value: unknown): void {
      if (disposed) return;
      failure = terminalError(value);
      connected = false;
      terminal.options.disableStdin = true;
      setError(failure); setPhase('error');
      clearTimeout(connectDeadline); clearInterval(keepalive); clearTimeout(resumeWindow);
      controller.abort(); socket?.close();
    }
    function sendResize(): void {
      if (connected && socket?.readyState === WebSocket.OPEN) socket.send(`\x1b[RESIZE:${terminal.cols};${terminal.rows}]`);
    }
    function resize(): void {
      frame = 0;
      if (disposed || element.clientWidth < 1 || element.clientHeight < 1) return;
      terminal.options.fontSize = fontSize(element.clientWidth);
      terminal.options.lineHeight = element.clientWidth < 1024 ? 1.02 : 1.15;
      fit.fit();
    }
    function scheduleResize(): void {
      if (!frame) frame = requestAnimationFrame(resize);
    }
    const observer = new ResizeObserver(scheduleResize);
    observer.observe(element);
    const viewport = window.visualViewport;
    viewport?.addEventListener('resize', scheduleResize);
    const resizeInput = terminal.onResize(sendResize);
    const scrollInput = terminal.onScroll(() => {
      followOutput = isViewportPinnedToBottom(terminal.buffer.active);
    });
    const dataInput = terminal.onData(data => {
      // Upstream Hermes also excludes terminal mouse reports from typed input.
      if (/^\x1b\[<\d+;\d+;\d+[Mm]$/.test(data)) return;
      if (connected && socket?.readyState === WebSocket.OPEN) socket.send(data);
    });
    terminal.attachCustomKeyEventHandler(event => {
      if (event.type !== 'keydown') return true;
      const modified = event.ctrlKey || event.metaKey;
      if (modified && event.key.toLowerCase() === 'v') return false;
      if (modified && event.key.toLowerCase() === 'c' && terminal.hasSelection()) {
        event.preventDefault();
        void navigator.clipboard.writeText(terminal.getSelection()).catch(value => {
          if (!disposed) setError(errorDetails(value));
        });
        return false;
      }
      if (event.key === 'Tab' && event.shiftKey) return false;
      return true;
    });

    connectDeadline = setTimeout(() => fail(new Error('The Hermes terminal did not connect within 30 seconds.')), 30_000);
    void (async () => {
      const fonts = await Promise.all([
        document.fonts.load('400 14px "JetBrains Mono"'),
        document.fonts.load('700 14px "JetBrains Mono"'),
        document.fonts.load('italic 400 14px "JetBrains Mono"'),
      ]);
      if (disposed) return;
      if (fonts.some(loaded => loaded.length === 0)) throw new Error('The Hermes terminal font did not load.');
      resize();
      const grant = await openTerminal(sessionId, agentId, attachmentId(accountId, sessionId, agentId), controller.signal);
      if (disposed) return;
      const url = new URL(grant.webSocketUrl, window.location.href);
      if (url.protocol !== 'wss:' || url.host !== window.location.host) throw new Error('The terminal address must use this website’s secure connection.');
      socket = new WebSocket(url);
      socket.binaryType = 'arraybuffer';
      socket.onopen = () => {
        if (!disposed) socket?.send(JSON.stringify({ticket: grant.ticket}));
      };
      socket.onmessage = event => {
        if (disposed) return;
        if (typeof event.data === 'string') {
          let control: unknown;
          try {control = JSON.parse(event.data);} catch {control = null;}
          if (control !== null && typeof control === 'object' && 'type' in control) {
            if (control.type === 'error') {fail('error' in control ? control.error : control); return;}
            if (control.type === 'ready') {
              connected = true; terminal.options.disableStdin = false;
              clearTimeout(connectDeadline); setPhase('open'); sendResize();
              keepalive = setInterval(sendResize, 20_000);
              // Hermes ChatPage uses this same bounded replay window.
              resumeWindow = setTimeout(() => resumeSanitizer.endEraseSuppression(), 30_000);
              return;
            }
            if (parseResumeControlMessage(event.data)) return;
          }
        } else if (!(event.data instanceof ArrayBuffer)) {
          fail(new Error('The Hermes terminal returned an unsupported WebSocket frame.')); return;
        }
        const text = typeof event.data === 'string' ? event.data : decoder.decode(new Uint8Array(event.data), {stream: true});
        const rendered = resumeSanitizer.next(text);
        terminal.write(rendered, shouldFollowPtyOutput(sessionId, followOutput) ? () => {
          if (!disposed) terminal.scrollToBottom();
        } : undefined);
      };
      socket.onerror = () => fail(new Error('The secure Hermes terminal connection failed. The browser did not supply further details.'));
      socket.onclose = event => {
        connected = false; terminal.options.disableStdin = true;
        clearTimeout(connectDeadline); clearInterval(keepalive); clearTimeout(resumeWindow);
        if (disposed || failure) return;
        terminal.write(resumeSanitizer.flush());
        setPhase('closed');
        if (event.code !== 1000 || event.reason) setError(errorDetails(new Error(`Hermes terminal closed: ${event.code}${event.reason ? ` — ${event.reason}` : ''}`)));
      };
    })().catch(value => {if (!disposed && !controller.signal.aborted) fail(value);});

    return () => {
      disposed = true; connected = false;
      controller.abort(); clearTimeout(connectDeadline); clearInterval(keepalive); clearTimeout(resumeWindow);
      if (frame) cancelAnimationFrame(frame);
      observer.disconnect(); viewport?.removeEventListener('resize', scheduleResize);
      dataInput.dispose(); resizeInput.dispose(); scrollInput.dispose(); clipboardGuard.dispose(); linkGuard.dispose();
      if (socket) {socket.onopen = null; socket.onmessage = null; socket.onerror = null; socket.onclose = null; socket.close();}
      terminal.dispose();
    };
  }, [accountId, sessionId, agentId, openTerminal, reconnect, helpId]);

  return <div className={`native-terminal${compact ? ' native-terminal-compact' : ''}`}>
    <Loading active={phase === 'connecting'}/>
    <div className="native-terminal-status">
      {(phase === 'closed' || phase === 'error') && <button type="button" className="text-button" onClick={() => setReconnect(value => value + 1)}>Reconnect terminal</button>}</div>
    <ErrorNotice error={error}/>
    <div className="native-terminal-canvas"><div ref={host} className="native-terminal-host" aria-label="Agent terminal"/></div>
    <p id={helpId} className="native-terminal-help">Type in the terminal. Enter sends your message. Shift+Tab moves focus out.</p>
  </div>;
}
